export default {
  id: 'entry-conditions',
  title: 'Entry Conditions',
  content: `## Entry Conditions

Entry conditions are the rules that determine when a strategy fires. You can add up to 12 conditions per strategy and combine them with AND (ALL) or OR (ANY) logic.

---

## Available Condition Types

---

### RSI

Checks the current RSI(14) value for the symbol.

| Operator | Example | Use case |
|---|---|---|
| Less than | RSI < 35 | Oversold zone — potential reversal |
| Greater than | RSI > 60 | Momentum zone — trend continuation |
| Between | RSI between 45–65 | Mid-range — avoid extended entries |

> RSI is computed from the daily market_features table — it reflects the RSI on the last completed trading day, updated nightly.

---

### MACD

Checks the current MACD signal direction for the symbol.

| Value | Condition |
|---|---|
| Bullish crossover | MACD line has crossed above signal line, histogram positive |
| Bearish crossover | MACD line has crossed below signal line, histogram negative |
| Bullish (histogram positive) | MACD is positive — bullish momentum regardless of recent crossover |

---

### Price vs SMA

Checks where the current price sits relative to a moving average.

| Condition | What it checks |
|---|---|
| Price above SMA20 | Short-term bullish — above 20-day average |
| Price above SMA50 | Medium-term bullish — in an uptrend |
| Price above SMA200 | Long-term bullish — above 200-day average |
| Price below SMA50 | Medium-term bearish — in a downtrend |

Using "Price above SMA50" as a trend filter is one of the most effective ways to avoid counter-trend trades.

---

### Volume Ratio

Checks the current session's volume relative to the 20-day average volume.

| Operator | Example | Use case |
|---|---|---|
| Greater than | Volume ratio > 1.5 | Confirm participation before entry |
| Greater than | Volume ratio > 2.5 | High conviction only |

Volume ratio is updated in real time from the current session's activity.

---

### Scanner Signal

Checks whether a specific scanner has fired for this symbol in the most recent scanner run (within the last 30 minutes for swing, within the last 5 minutes for intraday).

You can select from any of the 27+ named scanner signals including:
- BREAKOUT_20D_HIGH
- BREAKOUT_VOLUME_SPIKE
- CONSOLIDATION_BREAKOUT
- BULLISH_ENGULFING
- MACD_BULLISH_CROSSOVER
- RSI_OVERSOLD
- 52W_HIGH_TODAY
- ORB_BREAKOUT
- VWAP_RECLAIM
- And more

> Combining a Scanner signal condition with RSI and Volume conditions creates a highly selective, multi-confirmation strategy.

---

## AND vs OR Logic

| Mode | What it means | Result |
|---|---|---|
| ALL (AND) | Every condition must be true at the same time | Selective — fewer triggers, higher quality |
| ANY (OR) | At least one condition must be true | Broad — more triggers, lower average quality |

**Example using ALL:**
- RSI < 40 AND Volume ratio > 1.5 AND Price above SMA50

This fires only when the stock is mildly oversold, with above-average volume, in an uptrend — a much more specific setup than any single condition alone.

---

## Condition Evaluation Timing

Conditions are evaluated every minute during market hours using:
- **RSI, MACD, SMA values** — from the \`market_features\` database table, updated nightly with the prior day's close data
- **Volume ratio** — computed live from the current session's volume
- **Scanner signals** — from the most recent scanner run result
- **Current price** — from the live price feed (Redis)

This means RSI and MACD in Algo Flows reflect yesterday's close values, not intraday values. For intraday RSI, rely on scanner conditions instead.`,
};
